/* eslint-disable react/jsx-one-expression-per-line */
import React, { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import { GET_CONTENT_HISTORY } from 'queries/titleContent';
import MUIDataTable, {
  MUIDataTableColumnDef,
  MUIDataTableOptions,
} from 'mui-datatables';

import moment from 'moment';
import { Alert, AlertTitle } from '@material-ui/lab';
import { getStatusTextLabel } from '../../pages/labels';
import { StatusType } from '../../enums/status-type.enum';
import MUITableLoading from '../MUITableLoading/MUITableLoading';

const History = (titleId: string) => {
  const res = useQuery(GET_CONTENT_HISTORY, {
    variables: { titleId },
    fetchPolicy: 'network-only',
  });
  return res;
};

interface IContentHistoryProps {
  titleId: string;
}

interface IUserHistory {
  name: string;
  email: string;
  role: string;
}

interface IContentHistories {
  user: IUserHistory;
  duration: string;
  previousTitleStatus: StatusType;
  currentTitleStatus: StatusType;
  createdAt: Date;
}

const ContentHistory: React.FC<IContentHistoryProps> = ({ titleId }) => {
  const [history, setHistory] = useState<IContentHistories[]>([]);

  const { data, loading, error } = History(titleId);

  useEffect(() => {
    if (data) {
      const fetchedHistory = data.contentHistories.histories;
      setHistory(fetchedHistory);
    }
  }, [data]);

  const options: MUIDataTableOptions = {
    selectableRowsOnClick: false,
    selectableRowsHeader: false,
    selectableRows: 'none',
    responsive: 'standard',
    sort: false,
    filter: false,
    pagination: true,
  };

  const columns: MUIDataTableColumnDef[] = [
    {
      name: 'activity',
      label: 'ACTIVITY',
      options: {
        customBodyRender() {
          return 'Content status updated';
        },
      },
    },
    {
      name: 'oldTitleStatus',
      label: 'ACTIVITY DETAILS',
      options: {
        customBodyRender(cell: number, tableMeta) {
          return (
            <>
              Change status from:{' '}
              <strong>{`${
                getStatusTextLabel[
                  history[tableMeta.rowIndex].previousTitleStatus
                ]
              }`}</strong>{' '}
              to{' '}
              <strong>{`${
                getStatusTextLabel[
                  history[tableMeta.rowIndex].currentTitleStatus
                ]
              }`}</strong>
            </>
          );
        },
      },
    },
    {
      name: 'user',
      label: 'CHANGED BY',
      options: {
        customBodyRender(user: IUserHistory) {
          return user.name;
        },
      },
    },
    {
      name: 'duration',
      label: 'DURATION',
    },
    {
      name: 'updatedAt',
      label: 'DATE AND TIME',
      options: {
        customBodyRender(updatedAt: Date) {
          return moment(updatedAt).format('YYYY MMM DD, HH:mm a');
        },
      },
    },
  ];

  if (error) {
    return (
      <Alert severity="error">
        <AlertTitle>Error</AlertTitle>
        {error.message}
      </Alert>
    );
  }

  if (loading) {
    return (
      <MUITableLoading
        withRefresh
        hasFilter={false}
        selectable="none"
        columns={[
          'ACTIVITY',
          'ACTIVITY DETAILS',
          'CHANGED BY',
          'DURATION',
          'DATE AND TIME',
        ]}
      />
    );
  }

  return (
    <MUIDataTable
      title="History"
      data={history}
      columns={columns}
      options={options}
    />
  );
};

export default ContentHistory;
